import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from '../../../../ui/toast';
import { DIALOG_TYPE } from '../../../../constants/DialogTypes';
import { Button } from '../../../../ui/components/Common/Buttons/Button';
import { CustomSelect } from '../../../../ui/custom_react_select/custom_select';
import { TrashIcon } from '../../../../ui/icons/icons';
import { DatePicker } from '../../../../ui/input/date_picker/date_picker';
import { TextInput } from '../../../../ui/input/text_input/text_input';
import { ToggleInput } from '../../../../ui/input/toggle_input/toggle_input';
import { getFeatureCreditUsage, getOrganizationSubscriptions, updateLicense } from '../../../../utils/api_service/admin';
import { UsageMeter } from '../../../../ui/usage_meter/UsageMeter';
import { CANDI_BANK_CV, CreditUsageResponse, creditTone, formatResetDate } from '../../../../utils/types/credits_types';
import { getSubscriptionByID, getSubscriptions } from '../../../../utils/api_service/subscription_api';
import { LicenseDateRange, OrganizationLicenses } from '../../../../utils/types/admin_types';
import { OrganizationSubscriptionListResponse } from '../../types';

const BillingSubscriptionTab = () => {
  const [licenseState, setLicenseState] = useState<any>({
    hierTalentBase: { toggled: false, startDate: '2023-01-01', endDate: '2024-12-31' },
    autoAssessments: { toggled: false, startDate: '2023-01-01', endDate: '2024-12-31' },
    autoClassify: { toggled: false, startDate: null, endDate: null },
    jobPost: { toggled: false, startDate: '2023-06-15', endDate: '2024-06-14' },
    smartInterviews: { toggled: false, startDate: null, endDate: null },
    candiBank: { toggled: false, startDate: '2023-01-01', endDate: '2025-12-31' },
    cca: { toggled: false, startDate: '2023-01-01', endDate: '2025-12-31' },
    psychometricFaxtor: { toggled: false, startDate: '2023-01-01', endDate: '2025-12-31' },
    reporting: { toggled: false, startDate: '2023-01-01', endDate: '2025-12-31' },
  });

  const { id } = useParams<{ id: string }>();
  const [usage, setUsage] = useState<CreditUsageResponse | null>(null);

  const fetchUsage = async () => {
    try {
      const res = await getFeatureCreditUsage(id, CANDI_BANK_CV, 6);
      if (res.ok) setUsage(await res.json());
    } catch (e) {
      console.log(e);
    }
  };

  const [organizationLicenses, setOrganizationLicenses] = useState<OrganizationLicenses>({
    organizationId: id,
    hierTalentBase: undefined,
    hierTalentAutoAssessments: undefined,
    hierTalentAutoClassify: undefined,
    hierTalentJobPost: undefined,
    hierTalentSmartInterviews: undefined,
    hierTalentCandiBank: undefined,
    hierTalentCca: undefined,
    hierTalentPsychometricFaxtor: undefined,
    hierTalentReporting: undefined,
    hierTalentWellness: undefined,
  });

  const [overrides, setOverrides] = useState([
    { id: 1, product: 'HierTalent Base', interval: 'Annual', companySize: '100-500 employees', amount: '12,500.00' },
    { id: 2, product: 'CandiBank', interval: 'Annual', companySize: '100-500 employees', amount: '5,000.00' },
  ]);

  const LICENSE_KEY_MAP: Record<string, keyof Omit<OrganizationLicenses, 'organizationId'>> = {
    hierTalentBase: 'hierTalentBase',
    autoAssessments: 'hierTalentAutoAssessments',
    autoClassify: 'hierTalentAutoClassify',
    jobPost: 'hierTalentJobPost',
    smartInterviews: 'hierTalentSmartInterviews',
    candiBank: 'hierTalentCandiBank',
    cca: 'hierTalentCca',
    psychometricFaxtor: 'hierTalentPsychometricFaxtor',
    wellness: 'hierTalentWellness',
    reporting: 'hierTalentReporting',
  };

  const unixToDateString = (unix: number | string | undefined): string => {
    if (!unix) return '';
    return new Date(Number(unix) * 1000).toISOString().split('T')[0]; // "YYYY-MM-DD"
  };

  useEffect(() => {
    fetchSubscription();
    fetchUsage();
  }, [id]);

  const fetchSubscription = async () => {
    try {
      const res =  await getSubscriptionByID(id);

      if (res.ok) {
        const result = await res.json();

        setOrganizationLicenses({
          organizationId: id,
          hierTalentBase: result.hierTalentBase
            ? { startDate: unixToDateString(result.hierTalentBase.startDate), endDate: unixToDateString(result.hierTalentBase.endDate) }
            : undefined,
          hierTalentAutoAssessments: result.hierTalentAutoAssessments
            ? { startDate: unixToDateString(result.hierTalentAutoAssessments.startDate), endDate: unixToDateString(result.hierTalentAutoAssessments.endDate) }
            : undefined,
          hierTalentAutoClassify: result.hierTalentAutoClassify
            ? { startDate: unixToDateString(result.hierTalentAutoClassify.startDate), endDate: unixToDateString(result.hierTalentAutoClassify.endDate) }
            : undefined,
          hierTalentJobPost: result.hierTalentJobPost
            ? { startDate: unixToDateString(result.hierTalentJobPost.startDate), endDate: unixToDateString(result.hierTalentJobPost.endDate) }
            : undefined,
          hierTalentSmartInterviews: result.hierTalentSmartInterviews
            ? { startDate: unixToDateString(result.hierTalentSmartInterviews.startDate), endDate: unixToDateString(result.hierTalentSmartInterviews.endDate) }
            : undefined,
          hierTalentCandiBank: result.hierTalentCandiBank
            ? {
              startDate: unixToDateString(result.hierTalentCandiBank.startDate),
              endDate: unixToDateString(result.hierTalentCandiBank.endDate),
              monthlyCredits: result.hierTalentCandiBank.monthlyCredits ?? null,
            }
            : undefined,
          hierTalentCca: result.hierTalentCca
            ? { startDate: unixToDateString(result.hierTalentCca.startDate), endDate: unixToDateString(result.hierTalentCca.endDate) }
            : undefined,
          hierTalentPsychometricFaxtor: result.hierTalentPsychometricFaxtor
            ? { startDate: unixToDateString(result.hierTalentPsychometricFaxtor.startDate), endDate: unixToDateString(result.hierTalentPsychometricFaxtor.endDate) }
            : undefined,
          hierTalentReporting: result.hierTalentReporting
            ? { startDate: unixToDateString(result.hierTalentReporting.startDate), endDate: unixToDateString(result.hierTalentReporting.endDate) }
            : undefined,
          hierTalentWellness: result.hierTalentWellness
            ? { startDate: unixToDateString(result.hierTalentWellness.startDate), endDate: unixToDateString(result.hierTalentWellness.endDate) }
            : undefined,
        });

        console.log(result.subscriptions);
      }

    } catch (e) {
      console.log(e);
    }
  };

  const toggleLicense = (uiId: string) => {
    const key = LICENSE_KEY_MAP[uiId];
    setOrganizationLicenses((prev) => ({
      ...prev,
      // If currently active (has dates), set to undefined. If inactive, set empty strings as placeholder.
      [key]: prev[key] ? undefined : { startDate: '', endDate: '' },
    }));
  };

  const isSaveDisabled = Object.values(LICENSE_KEY_MAP).some((key) => {
    const license = organizationLicenses[key];
    return license !== undefined && (!license.startDate || !license.endDate);
  });

  const updateLicenseDate = (uiId: string, field: keyof LicenseDateRange, value: any) => {
    const key = LICENSE_KEY_MAP[uiId];

    let dateString: string;
    if (typeof value === 'object' && value !== null) {
      // Use the field-matching property if present, fallback to startDate
      dateString = value[field] ?? value.startDate;
    } else {
      dateString = value;
    }

    setOrganizationLicenses((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: dateString },
    }));
  };


  /** Metered licenses carry a monthly allowance. Blank = unlimited (null), 0 = blocked. */
  const monthlyCreditsValue = (uiId: string): string => {
    const value = organizationLicenses[LICENSE_KEY_MAP[uiId]]?.monthlyCredits;
    return value === null || value === undefined ? '' : String(value);
  };

  const updateMonthlyCredits = (uiId: string, raw: string) => {
    const key = LICENSE_KEY_MAP[uiId];
    const trimmed = raw.trim();
    const parsed = trimmed === '' ? null : Math.max(0, Math.floor(Number(trimmed)));
    setOrganizationLicenses((prev) => ({
      ...prev,
      [key]: { ...(prev[key] ?? { startDate: '', endDate: '' }), monthlyCredits: Number.isNaN(parsed) ? null : parsed },
    }));
  };

  const saveLicense = async () => {
    try {
      const res = await updateLicense(organizationLicenses);
      if (res.ok) {
        toast.success('License updated successfully.', { position: 'bottom-center' });
        fetchUsage();
      }
    } catch (e) {
      toast.error('Only one file allowed', { position: 'bottom-center' });
    }
  };

  return (
    <div className='space-y-8'>
      { /* Action Bar */ }
      <div className='flex justify-between items-center mb-4'>
        <h1 className='text-3xl font-black text-slate-800 tracking-tighter'>Manage Licenses & Subscriptions</h1>
        <div className='flex gap-3'>
          <Button label='Cancel' variant='secondary' className='rounded-xl px-6 py-2.5 font-bold border-none bg-slate-200/50 text-slate-600 hover:bg-slate-200' />
          <div className='relative group/save'>
            <Button
                onClick={saveLicense}
                label='Save Changes'
                variant='primary'
                disabled={isSaveDisabled}
                className='rounded-xl px-6 py-2.5 font-bold shadow-lg shadow-emerald-200/50 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none disabled:shadow-none'
            />
            { isSaveDisabled && (
              <div
                  className='absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-52 bg-slate-800 text-white text-[11px] font-medium text-center rounded-xl px-3 py-2 shadow-lg opacity-0 group-hover/save:opacity-100 transition-opacity pointer-events-none z-50'>
                Fill in start & end dates for all active licenses to save.
                <div
                    className='absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800'/>
              </div>
            ) }
          </div>
        </div>
      </div>

      { /* Quick Stats Cards */ }
      <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
        <div className='bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm'>
          <p className='text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1'>Org ID</p>
          <p className='text-xl font-black text-slate-800 tracking-tight'>{ id }</p>
        </div>
        <div className='bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm'>
          <p className='text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1'>Primary Admin</p>
          <p className='text-xl font-black text-slate-800 tracking-tight'>Sarah Jenkins</p>
        </div>
        <div className='bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm flex items-center gap-3'>
          <div className='w-2.5 h-2.5 rounded-full bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.5)]' />
          <div>
            <p className='text-[10px] font-black text-slate-400 uppercase tracking-widest'>Status</p>
            <p className='text-xl font-black text-emerald-600 tracking-tight'>Active</p>
          </div>
        </div>
      </div>

      { /* License Configuration */ }
      <div className='bg-white rounded-[2rem] border border-slate-100 shadow-sm p-8'>
        <div className='flex items-center gap-2 mb-8'>
          <svg className='w-5 h-5 text-emerald-500' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2.5}
                d='M9 12l2 2 4-4m5.618-4.016A3.323 3.323 0 0010.605 2.02a2.337 2.337 0 00-4.28 1.103 4.484 4.484 0 01.633 1.89h.001a1 1 0 001 1h8.423a1 1 0 011 1v8.423a1 1 0 001 1h.001a4.484 4.484 0 011.89.633 2.337 2.337 0 001.103-4.28 3.323 3.323 0 00-4.016-5.618z'/>
          </svg>
          <h2 className='text-lg font-black text-slate-800 tracking-tight text-[16px]'>License Configuration</h2>
        </div>

        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
          { [
            {
              id: 'hierTalentBase',
              label: 'HierTalent Base',
              icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10',
              color: 'bg-emerald-50 text-emerald-600',
            },
            {
              id: 'autoAssessments',
              label: 'Auto Assessments',
              icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
              color: 'bg-blue-50 text-blue-600',
            },
            {
              id: 'autoClassify',
              label: 'Auto Classify',
              icon: 'M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z',
              color: 'bg-indigo-50 text-indigo-600',
            },
            {
              id: 'jobPost',
              label: 'Job Post',
              icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
              color: 'bg-amber-50 text-amber-600',
            },
            {
              id: 'smartInterviews',
              label: 'Smart Interviews',
              icon: 'M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z',
              color: 'bg-rose-50 text-rose-600',
            },
            {
              id: 'candiBank',
              label: 'CandiBank',
              icon: 'M8 14v20M24 14v20M8 24h16',
              color: 'bg-slate-900 text-white',
              isSpecialIcon: true,
              creditUnit: 'CVs',
            },
            {
              id: 'cca',
              label: 'CCA',
              icon: 'M8 14v20M24 14v20M8 24h16',
              color: 'bg-slate-900 text-white',
              isSpecialIcon: true,
            },
            {
              id: 'psychometricFaxtor',
              label: 'Psychometric Faxtor',
              icon: 'M9 19V6l12-3v13M9 19a3 3 0 11-6 0 3 3 0 016 0zm12-3a3 3 0 11-6 0 3 3 0 016 0z',
              color: 'bg-purple-50 text-purple-600',
            },
            {
              id: 'reporting',
              label: 'Reporting',
              icon: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
              color: 'bg-cyan-50 text-cyan-600',
            },
            {
              id: 'wellness',
              label: 'Wellness',
              icon: 'M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 10-7.78 7.78l1.06 1.06L12 21l7.78-7.55 1.06-1.06a5.5 5.5 0 000-7.78z',
              color: 'bg-teal-50 text-teal-600',
            },
          ].map((item) => {
            const isActive = !!organizationLicenses[LICENSE_KEY_MAP[item.id]];
            return (
              <div
                  key={item.id}
                  className={`p-6 rounded-3xl border shadow-sm transition-all group relative ${
                    isActive
                      ? 'bg-white border-slate-100 hover:shadow-md hover:z-10'
                      : 'bg-slate-50 border-slate-100 opacity-60'
                  }`}
              >
                <div className='flex justify-between items-start mb-6'>
                  <div className='flex gap-4'>
                    <div
                        className={`w-12 h-12 rounded-2xl ${item.color} flex items-center justify-center shadow-inner`}>
                      { item.isSpecialIcon ? (
                        <svg className='w-6 h-6' fill='none' viewBox='0 0 32 32' stroke='currentColor'>
                          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2.5}
                              d='M11 6h10a2 2 0 012 2v16a2 2 0 01-2 2H11a2 2 0 01-2-2V8a2 2 0 012-2zM11 12h10M11 16h10M11 20h10'/>
                        </svg>
                      ) : (
                        <svg className='w-6 h-6' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d={item.icon}/>
                        </svg>
                      ) }
                    </div>
                    <div>
                      <h3 className='text-sm font-black text-slate-800 tracking-tight leading-tight'>{ item.label }</h3>
                      <p className='text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5'>Premium
                        Service</p>
                    </div>
                  </div>
                  <ToggleInput
                      toggled={isActive}
                      handleToggle={() => toggleLicense(item.id)}
                      name={item.label}
                  />
                </div>

                <div className={`grid grid-cols-2 gap-3 mt-auto ${!isActive ? 'pointer-events-none' : ''}`}>
                  <DatePicker
                      label='Start Date'
                      asSingle={true}
                      useRange={false}
                      startDate={organizationLicenses[LICENSE_KEY_MAP[item.id]]?.startDate ?? null}
                      endDate={organizationLicenses[LICENSE_KEY_MAP[item.id]]?.startDate ?? null}
                      onChange={(date) => updateLicenseDate(item.id, 'startDate', date)}
                  />
                  <DatePicker
                      label='End Date'
                      asSingle={true}
                      useRange={false}
                      startDate={organizationLicenses[LICENSE_KEY_MAP[item.id]]?.endDate ?? null}
                      endDate={organizationLicenses[LICENSE_KEY_MAP[item.id]]?.endDate ?? null}
                      onChange={(date) => updateLicenseDate(item.id, 'endDate', date)}
                  />
                </div>
                { item.creditUnit && (
                  <div className={`mt-3 ${!isActive ? 'pointer-events-none' : ''}`}>
                    <TextInput
                        label={`Credits / month (${item.creditUnit})`}
                        name={`${item.id}-monthly-credits`}
                        type='number'
                        min='0'
                        placeholder='Unlimited'
                        value={monthlyCreditsValue(item.id)}
                        onChange={(e) => updateMonthlyCredits(item.id, e.target.value)}
                    />
                    <p className='text-[10px] text-slate-400 font-medium mt-1'>Blank = unlimited · 0 = uploads blocked</p>
                  </div>
                ) }
              </div>
            );
          }) }
        </div>
      </div>

      { /* Credit usage (metered licenses) */ }
      { usage && (
        <div className='bg-white rounded-[2rem] border border-slate-100 shadow-sm p-8' data-testid='credit-usage-panel'>
          <div className='flex items-center justify-between mb-6'>
            <div>
              <h2 className='text-lg font-black text-slate-800 tracking-tight'>Credit usage · CandiBank {usage.unit}</h2>
              <p className='text-xs text-slate-400 font-medium mt-0.5'>
                { usage.unlimited
                  ? 'No monthly limit set — uploads are unlimited and still counted.'
                  : `${usage.monthlyLimit?.toLocaleString()} ${usage.unit} per month · resets ${formatResetDate(usage.current)}` }
              </p>
            </div>
            <span className={`text-[11px] font-black uppercase tracking-widest px-3 py-1 rounded-full ${
              creditTone(usage.current) === 'crit' ? 'bg-rose-50 text-rose-600'
                : creditTone(usage.current) === 'warn' ? 'bg-amber-50 text-amber-700'
                  : 'bg-emerald-50 text-emerald-600'
            }`}>
              { usage.unlimited ? 'Unlimited' : `${Math.min(100, Math.round((usage.current.used / Math.max(usage.monthlyLimit ?? 1, 1)) * 100))} % used` }
            </span>
          </div>
          <UsageMeter
              used={usage.current.used}
              limit={usage.current.monthlyLimit}
              tone={creditTone(usage.current)}
              leftLabel={`This month: ${usage.current.used.toLocaleString()} used`}
              rightLabel={usage.unlimited ? '' : `${(usage.current.remaining ?? 0).toLocaleString()} left`}
          />
          <div className='grid gap-3 mt-6' style={{ gridTemplateColumns: `repeat(${usage.history.length}, minmax(0, 1fr))` }}>
            { (() => {
              const peak = Math.max(usage.monthlyLimit ?? 0, ...usage.history.map((h) => h.used), 1);
              return usage.history.map((h) => (
                <div key={h.period} className='flex flex-col items-center gap-1.5'>
                  <div className='w-full h-16 flex items-end'>
                    <div
                        className={`w-full rounded-t-md ${h.period === usage.current.period ? 'bg-emerald-600' : 'bg-emerald-200'}`}
                        style={{ height: `${Math.max(4, Math.round((h.used / peak) * 100))}%` }}
                        title={`${h.period}: ${h.used}`}
                    />
                  </div>
                  <span className='text-[10px] font-mono font-bold text-slate-500'>{ h.period.slice(2) }</span>
                  <span className='text-[11px] font-bold text-slate-700 tabular-nums'>{ h.used.toLocaleString() }</span>
                </div>
              ));
            })() }
          </div>
        </div>
      ) }

      { /* Price Overrides */ }
      <div className='bg-white rounded-[2rem] border border-slate-100 shadow-sm overflow-hidden'>
        <div className='p-8 border-b border-slate-50 flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <svg className='w-5 h-5 text-emerald-600' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2.5}
                  d='M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z'/>
            </svg>
            <h2 className='text-lg font-black text-slate-800 tracking-tight'>Price Overrides</h2>
          </div>
          <button
              className='flex items-center gap-2 text-emerald-600 font-black text-[11px] uppercase tracking-widest hover:text-emerald-700 transition-colors'>
            <svg className='w-4 h-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={3} d='M12 4v16m8-8H4'/>
            </svg>
            Add Override
          </button>
        </div>
        <div className='overflow-x-auto'>
          <table className='w-full text-left'>
            <thead>
              <tr className='bg-slate-50/50'>
                <th className='px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest'>Product</th>
                <th className='px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest'>Interval</th>
                <th className='px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest'>Company Size
                </th>
                <th className='px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest'>Amount</th>
                <th className='px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right'>Actions</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-slate-50'>
              { overrides.map((override) => (
                <tr key={override.id} className='hover:bg-slate-50/30 transition-colors group'>
                  <td className='px-8 py-4'>
                    <div className='min-w-[200px]'>
                      <CustomSelect
                          name='product'
                          value={{ label: override.product, value: override.product }}
                          options={[{ label: override.product, value: override.product }]}
                          onChange={() => {
                          }}
                      />
                    </div>
                  </td>
                  <td className='px-8 py-4'>
                    <div className='min-w-[150px]'>
                      <CustomSelect
                          name='interval'
                          value={{ label: override.interval, value: override.interval }}
                          options={[{ label: 'Annual', value: 'Annual' }, { label: 'Monthly', value: 'Monthly' }]}
                          onChange={() => {
                          }}
                      />
                    </div>
                  </td>
                  <td className='px-8 py-4'>
                    <div className='min-w-[200px]'>
                      <CustomSelect
                          name='companySize'
                          value={{ label: override.companySize, value: override.companySize }}
                          options={[{ label: override.companySize, value: override.companySize }]}
                          onChange={() => {
                          }}
                      />
                    </div>
                  </td>
                  <td className='px-8 py-4'>
                    <div className='relative'>
                      <TextInput
                          name='amount'
                          value={override.amount}
                          type='text'
                          onChange={() => {
                          }}
                          className='pr-12'
                      />
                      <span
                          className='absolute right-4 top-1/2 -translate-y-1/2 text-[10px] font-black text-slate-300 uppercase'>USD</span>
                    </div>
                  </td>
                  <td className='px-8 py-4 text-right'>
                    <button
                        className='p-2.5 text-slate-300 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-all'>
                      <TrashIcon className='w-5 h-5'/>
                    </button>
                  </td>
                </tr>
              )) }
            </tbody>
          </table>
        </div>
      </div>

      { /* Administrative Actions */ }
      <div className='bg-rose-50/30 rounded-[2rem] border border-rose-100 p-8'>
        <h3 className='text-sm font-black text-slate-800 tracking-tight mb-2'>Administrative Actions</h3>
        <div className='flex flex-col md:flex-row justify-between items-start md:items-center gap-6'>
          <p className='text-sm text-slate-500 font-medium'>Suspend access immediately for this organization. This will
            affect all users.</p>
          <Button label='Suspend Organization' variant='primary' dialogType={DIALOG_TYPE.ERROR}
              className='rounded-xl px-8 py-3 font-bold shadow-lg shadow-rose-200/50'/>
        </div>
      </div>
    </div>
  );
};

export default BillingSubscriptionTab;
